import axios, { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// VITE_API_URL absolute base (e.g. https://api.rivalprop.com); dev uses Vite proxy.
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '') + '/api/v1'

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// CSRF — same pattern as trader-web. Token fetched lazily, auto-cleared on 403.
let csrfToken: string | null = null
async function ensureCsrfToken(): Promise<string | null> {
  if (csrfToken) return csrfToken
  try {
    const r = await axios.get(`${API_BASE}/auth/csrf-token`, { withCredentials: true })
    csrfToken = (r.data?.data?.token as string) ?? null
  } catch {
    csrfToken = null
  }
  return csrfToken
}
api.interceptors.request.use(async (cfg) => {
  const m = (cfg.method ?? 'get').toLowerCase()
  if (m === 'post' || m === 'put' || m === 'patch' || m === 'delete') {
    const token = await ensureCsrfToken()
    if (token) {
      cfg.headers = cfg.headers ?? {}
      ;(cfg.headers as Record<string, string>)['x-csrf-token'] = token
    }
  }
  return cfg
})
api.interceptors.response.use(
  (res) => res,
  (err: unknown) => {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      const code = (err.response.data as { error?: { code?: string } } | undefined)?.error?.code
      if (code === 'CSRF_FAILED') csrfToken = null
    }
    return Promise.reject(err)
  },
)

// ---------- Domain types ----------

export interface AdminMeUser {
  id: string
  email: string
  displayName: string
  role: string
  countryCode: string | null
  emailVerifiedAt: string | null
  locale: string | null
}

export interface AdminMeResponse {
  user: AdminMeUser
  account: unknown | null
}

export interface AdminKpis {
  newAccounts: number
  revenue: string
  payouts: string
  pendingPayouts: string
  activeFundedTraders: number
  openFraudSignals: number
  p3Queue: number
  windowFromIso: string
  windowToIso: string
}

export interface FunnelStep {
  step: string
  count: number
}

export interface FunnelPayload {
  windowFromIso: string
  windowToIso: string
  steps: FunnelStep[]
}

export interface AdminUserSummary {
  id: string
  email: string
  displayName: string
  role: string
  bannedAt: string | null
  banReason: string | null
  emailVerifiedAt: string | null
  countryCode: string | null
  createdAt: string
}

export interface AdminUserDetail {
  user: AdminUserSummary
  account: {
    id: string
    tierCode: string
    currentPhase: string
    startingBalance: string
    currentBalance: string
    startedAt: string | null
    closedAt: string | null
  } | null
  recentFraudSignals: AdminFraudSignal[]
  notes: Array<{
    id: string
    note: string
    actorId: string
    createdAt: string
  }>
}

export interface AdminFraudSignal {
  id: string
  userId: string | null
  signalType: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  details: Record<string, unknown> | null
  resolved: boolean
  resolution: string | null
  resolvedAt: string | null
  createdAt: string
}

export interface AdminReviewQueueItem {
  id: string
  userId: string
  accountId: string
  status: string
  assignedReviewerId: string | null
  assignedAt: string | null
  createdAt: string
  user: { email: string; displayName: string } | null
  composite: number | null
}

export interface AdminReviewDetail {
  review: {
    id: string
    userId: string
    accountId: string
    status: string
    assignedReviewerId: string | null
    assignedAt: string | null
    createdAt: string
    decisionOutcome: string | null
    decisionNotes: string | null
    decidedAt: string | null
  }
  user: AdminUserSummary
  account: {
    id: string
    tierCode: string
    currentPhase: string
    startingBalance: string
    currentBalance: string
  } | null
  latestScore: {
    composite: number
    components: {
      discipline: number
      edge: number
      consistency: number
      riskMgmt: number
      diversity: number
    }
    updatedAt: string
  } | null
  p1Stats: Record<string, unknown> | null
  p2Stats: Record<string, unknown> | null
  timeline: Array<{ at: string; kind: string; label: string; meta?: unknown }>
}

export type ReviewOutcome =
  | 'approved_funded'
  | 'approved_shortlist'
  | 'conditional_extra_verif'
  | 'rejected'
  | 'banned'

export interface AdminPayoutQueueItem {
  id: string
  userId: string
  cycleId: string
  amount: string
  status: string
  destination: string
  requestedAt: string
  approvedAt: string | null
  paidAt: string | null
  rejectedAt: string | null
  user: { email: string; displayName: string } | null
}

export interface AdminAuditEntry {
  id: string
  actorId: string | null
  action: string
  targetType: string
  targetId: string
  details: unknown
  createdAt: string
}

// ---------- Helpers ----------

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export function isAxiosError(err: unknown): err is AxiosError {
  return axios.isAxiosError(err)
}

export function apiErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: { message?: string }; message?: string } | undefined
    return data?.error?.message ?? data?.message ?? err.message
  }
  return err instanceof Error ? err.message : String(err)
}

export const ADMIN_ROLES = [
  'admin',
  'reviewer',
  'support',
  'finance',
  'super_admin',
] as const

export function isAdminRole(role: string | undefined | null): boolean {
  if (!role) return false
  return (ADMIN_ROLES as readonly string[]).includes(role)
}

// ---------- Auth ----------

export function useAdminMe() {
  return useQuery({
    queryKey: ['admin-me'],
    queryFn: async (): Promise<AdminMeResponse | null> => {
      try {
        const r = await api.get('/auth/me')
        return unwrap<AdminMeResponse>(r.data)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) return null
        throw err
      }
    },
    staleTime: 30_000,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      // POST /auth/login returns a flat shape ({userId, email, role, displayName}),
      // not the AdminMeResponse the AdminLoginPage expects. Adapt here so
      // `resp.user.role` works without changing the page.
      const r = await api.post('/auth/login', input)
      const raw = unwrap<{ userId: string; email: string; role: string; displayName: string | null }>(r.data)
      return {
        user: {
          id:          raw.userId,
          email:       raw.email,
          role:        raw.role,
          displayName: raw.displayName,
          locale:      null,
        },
        account: null,
      } as AdminMeResponse
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-me'] })
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post('/auth/logout')
    },
    onSuccess: () => {
      qc.clear()
    },
  })
}

// ---------- Overview ----------

export function useAdminOverviewKpis(window?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ['admin-kpis', window],
    queryFn: async () => {
      const r = await api.get('/admin/overview/kpis', { params: window })
      return unwrap<AdminKpis>(r.data)
    },
    refetchInterval: 60_000,
  })
}

export function useAdminFunnel(window?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ['admin-funnel', window],
    queryFn: async () => {
      const r = await api.get('/admin/overview/funnel', { params: window })
      return unwrap<FunnelPayload>(r.data)
    },
  })
}

// ---------- Users ----------

export interface UserListParams {
  email?: string
  role?: string
  banned?: boolean
  page?: number
  perPage?: number
}

export function useAdminUsers(params: UserListParams) {
  return useQuery({
    queryKey: ['admin-users', params],
    queryFn: async () => {
      const r = await api.get('/admin/users', { params })
      return unwrap<{ users: AdminUserSummary[] }>(r.data)
    },
  })
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-user', id],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get(`/admin/users/${id}`)
      return unwrap<AdminUserDetail>(r.data)
    },
  })
}

export function useBanUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const r = await api.post(`/admin/users/${input.id}/ban`, { reason: input.reason })
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user', vars.id] })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useUnbanUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const r = await api.post(`/admin/users/${input.id}/unban`, {})
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user', vars.id] })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useAddUserNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; note: string }) => {
      const r = await api.post(`/admin/users/${input.id}/note`, { note: input.note })
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user', vars.id] })
    },
  })
}

// ---------- Reviews ----------

export interface ReviewQueueParams {
  status?: string
  assignedTo?: string
  page?: number
  perPage?: number
}

export function useAdminReviewQueue(params: ReviewQueueParams) {
  return useQuery({
    queryKey: ['admin-review-queue', params],
    queryFn: async () => {
      const r = await api.get('/admin/review/queue', { params })
      return unwrap<{ reviews: AdminReviewQueueItem[] }>(r.data)
    },
  })
}

export function useAdminReview(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['admin-review', reviewId],
    enabled: !!reviewId,
    queryFn: async () => {
      const r = await api.get(`/admin/review/${reviewId}`)
      return unwrap<AdminReviewDetail>(r.data)
    },
  })
}

export function useAssignReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { reviewId: string }) => {
      const r = await api.post(`/admin/review/${input.reviewId}/assign`, {})
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-review', vars.reviewId] })
      void qc.invalidateQueries({ queryKey: ['admin-review-queue'] })
    },
  })
}

export interface DecideReviewInput {
  reviewId: string
  outcome: ReviewOutcome
  decisionNotes: string
  conditionalExtraBetCount?: number
  flags?: string[]
}

export function useDecideReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: DecideReviewInput) => {
      const { reviewId, ...body } = input
      const r = await api.post(`/admin/review/${reviewId}/decide`, body)
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-review', vars.reviewId] })
      void qc.invalidateQueries({ queryKey: ['admin-review-queue'] })
    },
  })
}

// ---------- Fraud ----------

export interface FraudListParams {
  severity?: 'low' | 'medium' | 'high' | 'critical'
  signalType?: string
  unresolved?: boolean
  userId?: string
  page?: number
  perPage?: number
}

export function useAdminFraudSignals(params: FraudListParams) {
  return useQuery({
    queryKey: ['admin-fraud', params],
    queryFn: async () => {
      const r = await api.get('/admin/fraud/signals', { params })
      return unwrap<{ signals: AdminFraudSignal[] }>(r.data)
    },
  })
}

export function useAdminFraudSignal(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-fraud-detail', id],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get(`/admin/fraud/signals/${id}`)
      return unwrap<{ signal: AdminFraudSignal }>(r.data)
    },
  })
}

export function useResolveFraudSignal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      resolution: 'confirmed_fraud' | 'false_positive' | 'requires_more_info'
      notes?: string
    }) => {
      const r = await api.post(`/admin/fraud/signals/${input.id}/resolve`, {
        resolution: input.resolution,
        notes: input.notes,
      })
      return unwrap(r.data)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-fraud-detail', vars.id] })
      void qc.invalidateQueries({ queryKey: ['admin-fraud'] })
    },
  })
}

// ---------- Payouts ----------

export interface PayoutQueueParams {
  status?: string
  page?: number
  perPage?: number
}

export function useAdminPayoutQueue(params: PayoutQueueParams) {
  return useQuery({
    queryKey: ['admin-payout-queue', params],
    queryFn: async () => {
      const r = await api.get('/admin/payouts/queue', { params })
      return unwrap<{ payouts: AdminPayoutQueueItem[] }>(r.data)
    },
  })
}

export function useApprovePayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; notes?: string }) => {
      const r = await api.post(`/admin/payouts/${input.id}/approve`, { notes: input.notes })
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-payout-queue'] })
    },
  })
}

export function useRejectPayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const r = await api.post(`/admin/payouts/${input.id}/reject`, { reason: input.reason })
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-payout-queue'] })
    },
  })
}

export function useMarkPayoutPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; externalPaymentId: string }) => {
      const r = await api.post(`/admin/payouts/${input.id}/mark-paid`, {
        externalPaymentId: input.externalPaymentId,
      })
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-payout-queue'] })
    },
  })
}

// ---------- Audit ----------

export interface AuditFilterParams {
  targetType?: string
  targetId?: string
  actorId?: string
  action?: string
  from?: string
  to?: string
  page?: number
  perPage?: number
}

export function useAdminAuditLog(params: AuditFilterParams) {
  return useQuery({
    queryKey: ['admin-audit', params],
    queryFn: async () => {
      const r = await api.get('/admin/audit', { params })
      return unwrap<{ entries: AdminAuditEntry[] }>(r.data)
    },
  })
}

export function useAdminAuditExport() {
  return useMutation({
    mutationFn: async (params: AuditFilterParams): Promise<string> => {
      const r = await api.get('/admin/audit/export', {
        params,
        responseType: 'blob',
      })
      const blob = r.data as Blob
      return URL.createObjectURL(blob)
    },
  })
}
